import React from 'react';
import Navigation from '../components/shared/Navigation';
import Footer from '../components/shared/Footer';

export default function FairHousingPage() {
  return (
    <div>
      <Navigation />
      
      <div className="container py-24 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-[var(--font-display)] tracking-[-0.02em] mb-4">
          Fair Housing Statement
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          Ellis Row is committed to equal housing opportunity
        </p>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">Our Commitment to Fair Housing</h2>
            <p className="text-muted-foreground leading-relaxed">
              Ellis Row, a development by Mods Enterprises, is committed to compliance with all federal, state, and local fair housing laws. 
              We are proud to provide equal housing opportunities to all persons regardless of race, color, religion, sex, disability, 
              familial status, national origin, sexual orientation, gender identity, or any other characteristic protected by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">Federal Fair Housing Act</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              The Fair Housing Act (Title VIII of the Civil Rights Act of 1968) prohibits discrimination in the sale, rental, and 
              financing of dwellings based on the following protected classes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Race</li>
              <li>Color</li>
              <li>National Origin</li>
              <li>Religion</li>
              <li>Sex (including sexual orientation and gender identity)</li>
              <li>Familial Status (families with children under 18)</li>
              <li>Disability</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">Equal Opportunity Housing</h2>
            <div className="bg-muted/30 border-l-4 border-[#A06A3B] p-6 rounded-r-lg">
              <p className="text-lg font-semibold mb-2">
                🏡 Equal Housing Opportunity
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Ellis Row provides housing on a nondiscriminatory basis. All applications are evaluated using the same objective criteria 
                related to creditworthiness, income, rental history, and background. We do not discriminate in any aspect of the 
                rental or sale process.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">Reasonable Accommodations</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We are committed to making reasonable accommodations and modifications for individuals with disabilities. If you require 
              a reasonable accommodation or modification to fully utilize our housing, please contact us at info@liveellisrow.com.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Examples of reasonable accommodations may include:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Assistance animals for persons with disabilities</li>
              <li>Accessible parking spaces</li>
              <li>Modified application procedures</li>
              <li>Physical modifications to the unit (at tenant's expense for rentals, or as negotiated for purchases)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">Our Application Process</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              All applicants are evaluated using consistent, objective criteria that are applied equally to all prospective residents. 
              Our screening criteria include:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Credit history</li>
              <li>Income verification (must meet income requirements)</li>
              <li>Rental history and references</li>
              <li>Background check (criminal history review in compliance with applicable laws)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We do not consider protected class status in our application decisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">Accessibility Features</h2>
            <p className="text-muted-foreground leading-relaxed">
              Ellis Row is designed to meet all applicable accessibility requirements. Our units feature:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Accessible entryways and common areas</li>
              <li>Wide doorways (7'5" interior doors)</li>
              <li>Accessible electrical outlets and controls</li>
              <li>Compliance with state and federal accessibility standards</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">How to Report Discrimination</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you believe you have experienced housing discrimination, you have the right to file a complaint. You may file a complaint with:
            </p>
            
            <div className="space-y-4 mt-6">
              <div className="bg-white border border-border p-5 rounded-lg">
                <h3 className="font-semibold mb-2">U.S. Department of Housing and Urban Development (HUD)</h3>
                <p className="text-sm text-muted-foreground">
                  Office of Fair Housing and Equal Opportunity<br />
                  Phone: 1-800-669-9777<br />
                  TTY: 1-800-927-9275<br />
                  Website: www.hud.gov/fairhousing<br />
                  Online Complaint: www.hud.gov/program_offices/fair_housing_equal_opp/online-complaint
                </p>
              </div>

              <div className="bg-white border border-border p-5 rounded-lg">
                <h3 className="font-semibold mb-2">Iowa Civil Rights Commission</h3>
                <p className="text-sm text-muted-foreground">
                  Phone: 515-281-4121<br />
                  Toll-Free: 1-800-457-4416<br />
                  Website: icrc.iowa.gov
                </p>
              </div>

              <div className="bg-white border border-border p-5 rounded-lg">
                <h3 className="font-semibold mb-2">Contact Us Directly</h3>
                <p className="text-sm text-muted-foreground">
                  Email: info@liveellisrow.com<br />
                  We take all complaints seriously and will investigate promptly.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">Familial Status</h2>
            <p className="text-muted-foreground leading-relaxed">
              Families with children under the age of 18 are welcome at Ellis Row. We do not discriminate against families with children 
              and provide equal access to all amenities and services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">Language Assistance</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you need assistance in a language other than English, please contact us at info@liveellisrow.com, and we will make 
              reasonable efforts to provide language assistance services.
            </p>
          </section>

          <div className="bg-[#121416] text-white p-8 rounded-lg mt-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl">⚖️</div>
              <h3 className="text-2xl font-[var(--font-display)]">Equal Housing Opportunity</h3>
            </div>
            <p className="leading-relaxed">
              Ellis Row is an Equal Opportunity Housing Provider. We comply with all federal, state, and local fair housing laws 
              and do not discriminate on the basis of any protected class.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
